import { useEffect, useMemo, useState } from 'react'
import {
  CheckCircle2,
  Loader2,
  PhoneIncoming,
  PhoneOutgoing,
  XCircle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useProfile } from '@/api/profile'
import {
  useAIAgentMissions,
  useStartTestCall,
  useTestCallStatus,
} from '@/api/aiAgents'

interface Props {
  isOpen: boolean
  onClose: () => void
  companyId: number | undefined
  agentId: number
  agentName: string
  /**
   * État de l'enregistrement déclenché par « Tester » dans le formulaire.
   * La modale s'ouvre PENDANT la sauvegarde, pas après : le client saisit
   * son numéro et choisit sa mission pendant que la configuration part —
   * dans la plupart des cas, l'attente a disparu avant qu'il ait fini de
   * taper. Absent = la modale vient de la liste, aucune étape à montrer.
   */
  saveState?: 'saving' | 'saved' | 'error'
  /** Relance la sauvegarde après un échec, sans fermer la modale. */
  onRetrySave?: () => void
}

type StepState = 'done' | 'current' | 'todo' | 'error'

/**
 * Barre déterminée pour l'enregistrement. Une barre qui stagne est perçue
 * comme une panne : celle-ci avance en s'amortissant vers ~85 % (estimation),
 * puis saute à 100 % dès que le serveur répond (fait). Elle ne recule et ne
 * s'arrête jamais.
 */
function SaveProgressBar({ done }: { done: boolean }) {
  const [width, setWidth] = useState(4)
  useEffect(() => {
    // Lancer la transition APRÈS le premier paint, sinon la barre naît à 85 %.
    const id = requestAnimationFrame(() => setWidth(85))
    return () => cancelAnimationFrame(id)
  }, [])
  return (
    <div className="h-1 w-full rounded-full bg-gray-100 dark:bg-slate-800 overflow-hidden">
      <div
        className="h-full rounded-full bg-blue-600 dark:bg-blue-500"
        style={{
          width: `${done ? 100 : width}%`,
          transition: done
            ? 'width 0.25s ease-out'
            : 'width 7s cubic-bezier(0.1, 0.6, 0.2, 1)',
        }}
      />
    </div>
  )
}

/**
 * Fil des étapes : rendre visible ce que le produit fait à la place du
 * client (enregistrer, appeler, restituer) plutôt que de le laisser
 * attendre devant un écran figé.
 */
function Steps({ steps }: { steps: { label: string; state: StepState }[] }) {
  return (
    <ol className="flex items-center gap-2 text-xs">
      {steps.map((step, i) => (
        <li key={step.label} className="flex items-center gap-2 min-w-0">
          {i > 0 && (
            <span className="h-px w-4 bg-gray-200 dark:bg-slate-700 flex-shrink-0" />
          )}
          <span
            className={
              step.state === 'done'
                ? 'flex items-center gap-1.5 text-green-700 dark:text-green-500'
                : step.state === 'current'
                  ? 'flex items-center gap-1.5 font-medium text-blue-700 dark:text-blue-400'
                  : step.state === 'error'
                    ? 'flex items-center gap-1.5 font-medium text-red-600 dark:text-red-400'
                    : 'flex items-center gap-1.5 text-gray-400 dark:text-slate-500'
            }
          >
            {step.state === 'done' ? (
              <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />
            ) : step.state === 'current' ? (
              <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin" />
            ) : step.state === 'error' ? (
              <XCircle className="h-3.5 w-3.5 flex-shrink-0" />
            ) : (
              <span className="h-1.5 w-1.5 rounded-full bg-current flex-shrink-0" />
            )}
            <span className="truncate">{step.label}</span>
          </span>
        </li>
      ))}
    </ol>
  )
}

/**
 * Modale « Appelez-moi » — l'agent IA appelle le téléphone du manager pour
 * qu'il entende sa configuration réelle. Déclenche l'appel puis suit le
 * statut en polling ; le transcript/résumé peuvent arriver quelques secondes
 * après la fin de l'appel (récupération ElevenLabs asynchrone).
 */
export function TestCallModal({
  isOpen,
  onClose,
  companyId,
  agentId,
  agentName,
  saveState,
  onRetrySave,
}: Props) {
  const { t } = useTranslation()
  const { data: profile } = useProfile()
  const startCall = useStartTestCall(companyId)
  const { data: missions = [] } = useAIAgentMissions(companyId, agentId)
  const activeMissions = missions.filter((m) => m.is_active)

  const [phone, setPhone] = useState('')
  const [missionId, setMissionId] = useState<number | null>(null)
  const [missionContext, setMissionContext] = useState('')
  const [callId, setCallId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Nombre de polls effectués après la fin de l'appel, pour arrêter
  // d'attendre un transcript qui ne viendra pas (appel très court, échec…).
  const [pollsAfterEnd, setPollsAfterEnd] = useState(0)

  // Réinitialiser à l'ouverture
  useEffect(() => {
    if (isOpen) {
      setPhone(profile?.phone_number ?? '')
      setMissionId(null)
      setMissionContext('')
      setCallId(null)
      setError(null)
      setPollsAfterEnd(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Pré-remplissage du numéro personnel, séparé du reset : si le profil
  // n'était pas encore en cache à l'ouverture, l'effet ci-dessus laissait le
  // champ vide pour de bon. Ici on remplit dès que le profil arrive — mais
  // JAMAIS par-dessus une saisie du client.
  useEffect(() => {
    if (isOpen && !phone && profile?.phone_number) {
      setPhone(profile.phone_number)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, profile?.phone_number])

  // Le sous-texte de l'enregistrement suit les deux phases réelles du PATCH
  // (écriture en base, puis préparation de la config vocale) sans jamais
  // nommer de fournisseur. Pas de signal serveur par phase : bascule ~2 s.
  const [savingPhase, setSavingPhase] = useState(0)
  useEffect(() => {
    if (saveState !== 'saving') return
    setSavingPhase(0)
    const id = setTimeout(() => setSavingPhase(1), 2000)
    return () => clearTimeout(id)
  }, [saveState])

  // La barre reste affichée ~600 ms après le succès, le temps de sauter à
  // 100 % : disparaître à 85 % se lirait comme une interruption.
  const [showSaveBar, setShowSaveBar] = useState(false)
  useEffect(() => {
    if (saveState === 'saving') {
      setShowSaveBar(true)
      return
    }
    if (saveState === 'saved' && showSaveBar) {
      const id = setTimeout(() => setShowSaveBar(false), 600)
      return () => clearTimeout(id)
    }
    setShowSaveBar(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveState])

  const { data: call } = useTestCallStatus(companyId, agentId, callId ?? undefined, {
    // Poll tant que l'appel est actif, puis encore ~30s après la fin
    // le temps que le transcript arrive. false = stop.
    refetchInterval: callId && pollsAfterEnd < 15 ? 2000 : false,
  })

  const isTerminal = call?.status === 'completed' || call?.status === 'failed'
  const hasTranscript = !!(call?.transcript || call?.conversation_summary)

  useEffect(() => {
    if (!call) return
    if (isTerminal && !hasTranscript) {
      setPollsAfterEnd((n) => n + 1)
    }
    if (isTerminal && hasTranscript) {
      setPollsAfterEnd(15) // stop le polling
    }
  }, [call, isTerminal, hasTranscript])

  const handleStart = async () => {
    setError(null)
    try {
      const res = await startCall.mutateAsync({
        agentId,
        phoneNumber: phone.trim() || undefined,
        missionId: missionId ?? undefined,
        missionContext: missionId ? missionContext.trim() : undefined,
      })
      setCallId(res.call_id)
      setPollsAfterEnd(0)
    } catch (e) {
      const code =
        e instanceof AxiosError ? (e.response?.data?.error as string) : null
      setError(
        code && ERROR_KEYS.includes(code)
          ? t(`manager.aiAgents.testCall.errors.${code}`)
          : t('manager.aiAgents.testCall.errors.generic'),
      )
    }
  }

  const statusLabel = useMemo(() => {
    if (!call) return t('manager.aiAgents.testCall.status.calling')
    switch (call.status) {
      case 'initiated':
        return t('manager.aiAgents.testCall.status.calling')
      case 'ringing':
        return t('manager.aiAgents.testCall.status.ringing')
      case 'answered':
        return t('manager.aiAgents.testCall.status.inProgress')
      case 'completed':
        return t('manager.aiAgents.testCall.status.completed')
      default:
        return t('manager.aiAgents.testCall.status.failed')
    }
  }, [call, t])

  // L'écoute est « en cours » dès que l'appel est lancé et jusqu'à sa fin ;
  // le compte-rendu devient l'étape courante ensuite, et n'est acquis qu'une
  // fois le transcript ou le résumé effectivement arrivé.
  const steps: { label: string; state: StepState }[] = [
    ...(saveState
      ? [
          {
            label:
              saveState === 'saved'
                ? t('manager.aiAgents.testCall.steps.saved')
                : t('manager.aiAgents.testCall.steps.saving'),
            state: (saveState === 'saved'
              ? 'done'
              : saveState === 'error'
                ? 'error'
                : 'current') as StepState,
          },
        ]
      : []),
    {
      label: t('manager.aiAgents.testCall.steps.call'),
      // Une seule étape courante à la fois : tant que l'enregistrement
      // tourne (ou a échoué), l'appel est « à venir », pas « en cours ».
      state:
        saveState === 'saving' || saveState === 'error'
          ? 'todo'
          : !callId
            ? 'current'
            : isTerminal
              ? 'done'
              : 'current',
    },
    {
      label: t('manager.aiAgents.testCall.steps.result'),
      state: !isTerminal ? 'todo' : hasTranscript ? 'done' : 'current',
    },
  ]

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneOutgoing className="h-5 w-5 text-blue-600" />
            {t('manager.aiAgents.testCall.title', { name: agentName })}
          </DialogTitle>
          <DialogDescription>
            {t('manager.aiAgents.testCall.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="border-y border-gray-100 dark:border-slate-800 py-2.5 -mx-6 px-6 space-y-2">
          <div className="overflow-x-auto">
            <Steps steps={steps} />
          </div>
          {/* Barre déterminée + phase en cours : l'attente est expliquée,
              et le reste de la modale reste saisissable pendant ce temps. */}
          {showSaveBar && (
            <div className="space-y-1">
              <SaveProgressBar done={saveState === 'saved'} />
              {saveState === 'saving' && (
                <p className="text-[11px] text-gray-500 dark:text-slate-400">
                  {savingPhase === 0
                    ? t('manager.aiAgents.testCall.savingPhase1')
                    : t('manager.aiAgents.testCall.savingPhase2')}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Échec de l'enregistrement : affiché dans la modale (pas un toast
            qui disparaît), avec la relance sur place. L'appel est bloqué tant
            que la configuration testée n'est pas celle qui vient d'être
            écrite. */}
        {saveState === 'error' && (
          <div className="flex items-start justify-between gap-3 rounded-md bg-red-50 dark:bg-red-950/40 p-3">
            <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400">
              <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{t('manager.aiAgents.testCall.saveFailed')}</span>
            </div>
            {onRetrySave && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onRetrySave}
                className="flex-shrink-0"
              >
                {t('manager.aiAgents.testCall.retrySave')}
              </Button>
            )}
          </div>
        )}

        {/* Étape 1 : saisie du numéro */}
        {!callId && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="test-call-phone">
                {t('manager.aiAgents.testCall.phoneLabel')}
              </Label>
              <Input
                id="test-call-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 514 555 1234"
                autoComplete="tel"
              />
              <p className="text-xs text-gray-500 dark:text-slate-400">
                {/* Dire d'où vient la valeur : un champ prérempli sans
                    explication se lit comme une donnée à vérifier. */}
                {phone && phone === profile?.phone_number
                  ? t('manager.aiAgents.testCall.personalNumberHint')
                  : t('manager.aiAgents.testCall.phoneHint')}
              </p>
            </div>

            {activeMissions.length > 0 && (
              <div className="space-y-2">
                <Label>{t('manager.aiAgents.testCall.missionLabel')}</Label>
                <Select
                  value={missionId ? String(missionId) : 'none'}
                  onValueChange={(v) => {
                    setMissionId(v === 'none' ? null : Number(v))
                    setMissionContext('')
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      {t('manager.aiAgents.testCall.noMission')}
                    </SelectItem>
                    {activeMissions.map((m) => (
                      <SelectItem key={m.id} value={String(m.id)}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Reproduit la précision que porterait la branche du flux :
                    le test entend exactement ce que l'appelant entendra. */}
                {missionId && (
                  <div className="space-y-1">
                    <Input
                      value={missionContext}
                      onChange={(e) => setMissionContext(e.target.value)}
                      placeholder={t(
                        'manager.aiAgents.testCall.missionContextPlaceholder',
                      )}
                      className="text-sm"
                    />
                    <p className="text-xs text-gray-500 dark:text-slate-400">
                      {t('manager.aiAgents.testCall.missionContextHint')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-700 dark:text-red-400">
                <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                {t('common.cancel')}
              </Button>
              {/* Tant que la sauvegarde tourne, l'appel testerait l'ancienne
                  version : le bouton l'annonce plutôt que d'être muet. */}
              <Button
                type="button"
                onClick={() => void handleStart()}
                disabled={
                  startCall.isPending ||
                  !phone.trim() ||
                  saveState === 'saving' ||
                  saveState === 'error'
                }
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {startCall.isPending || saveState === 'saving' ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <PhoneIncoming className="h-4 w-4 mr-2" />
                )}
                {saveState === 'saving'
                  ? t('manager.aiAgents.testCall.readySoon')
                  : t('manager.aiAgents.testCall.callMe')}
              </Button>
            </div>
          </div>
        )}

        {/* Étape 2 : appel en cours / terminé */}
        {callId && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-slate-700 p-4">
              {!isTerminal ? (
                <Loader2 className="h-5 w-5 animate-spin text-blue-600 flex-shrink-0" />
              ) : call?.status === 'completed' ? (
                <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {statusLabel}
                </div>
                {call?.duration != null && isTerminal && (
                  <div className="text-xs text-gray-500 dark:text-slate-400">
                    {t('manager.aiAgents.testCall.duration', {
                      seconds: call.duration,
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Transcript en attente après la fin de l'appel */}
            {isTerminal && !hasTranscript && pollsAfterEnd < 15 && (
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t('manager.aiAgents.testCall.waitingTranscript')}
              </div>
            )}

            {call?.conversation_summary && (
              <div className="space-y-1.5">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  {t('manager.aiAgents.testCall.summary')}
                </div>
                <p className="text-sm text-gray-700 dark:text-slate-300 rounded-md bg-gray-50 dark:bg-slate-800/60 p-3">
                  {call.conversation_summary}
                </p>
              </div>
            )}

            {call?.transcript && (
              <div className="space-y-1.5">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                  {t('manager.aiAgents.testCall.transcript')}
                </div>
                <pre className="text-xs text-gray-700 dark:text-slate-300 whitespace-pre-wrap rounded-md bg-gray-50 dark:bg-slate-800/60 p-3 max-h-56 overflow-y-auto font-sans">
                  {call.transcript}
                </pre>
              </div>
            )}

            <div className="flex gap-3">
              {isTerminal && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setCallId(null)
                    setError(null)
                    setPollsAfterEnd(0)
                  }}
                  className="flex-1"
                >
                  {t('manager.aiAgents.testCall.callAgain')}
                </Button>
              )}
              <Button
                type="button"
                variant={isTerminal ? 'default' : 'outline'}
                onClick={onClose}
                className="flex-1"
              >
                {t('common.close')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

const ERROR_KEYS = [
  'agent_inactive',
  'agent_not_synced',
  'no_phone_number',
  'invalid_phone_number',
  'no_from_number',
  'test_call_in_progress',
  'service_suspended',
  'payment_suspended',
]
